import type { MyGroupUsageResponse } from "../../api/organisationReporting";
import { formatWholeMoney } from "../consumption/formatters";
import card from "../tools/ToolCard.module.css";
import type { GroupPace, GroupStatus } from "./groupPace";

/** The words shared by a group's card on My Groups and its own page: its kind, the viewer's role, its status and its summary sentence. */

export const kindKeys: Record<MyGroupUsageResponse["kind"], string> = {
  Division: "myGroups.kind.division",
  Department: "myGroups.kind.department",
  Group: "myGroups.kind.group",
};

export const statusTones: Record<GroupStatus, string> = {
  onTrack: card.chipSuccess,
  within: card.chipSuccess,
  close: card.chipWarning,
  heading: card.chipDanger,
  over: card.chipDanger,
  none: card.chipNeutral,
};

export type Translate = (key: string, options?: Record<string, unknown>) => string;

export function statusText(t: Translate, pace: GroupPace): string {
  switch (pace.status) {
    case "heading":
      return t("myGroups.card.status.heading", { percent: pace.overPercent });
    case "over":
      return t("myGroups.card.status.over", { percent: pace.overPercent });
    default:
      return t(`myGroups.card.status.${pace.status}`);
  }
}

/** One sentence on where the group is heading, and what it can spend per working day to stay within its allowance. */
export function summaryText(t: Translate, pace: GroupPace, currency: string): string {
  const money = (amount: number): string => formatWholeMoney(amount, currency);
  const allowance = pace.allowance;
  const projected = pace.projected;
  if (allowance === null) {
    return projected !== null ? t("myGroups.card.summary.noAllowance", { projected: money(projected) }) : t("myGroups.card.summary.past", { spend: money(pace.spend) });
  }
  switch (pace.status) {
    case "over":
      return t(projected !== null ? "myGroups.card.summary.over" : "myGroups.card.summary.overPast", {
        over: money(pace.spend - allowance),
        allowance: money(allowance),
        count: pace.remainingWorkingDays,
      });
    case "heading":
      return t("myGroups.card.summary.heading", {
        projected: money(projected ?? 0),
        over: money((projected ?? 0) - allowance),
        headroom: money(pace.headroomPerWorkingDay ?? 0),
      });
    case "close":
      return t("myGroups.card.summary.close", { projected: money(projected ?? 0), allowance: money(allowance), headroom: money(pace.headroomPerWorkingDay ?? 0) });
    case "onTrack":
      return t("myGroups.card.summary.onTrack", { projected: money(projected ?? 0), allowance: money(allowance) });
    default:
      return t("myGroups.card.summary.within", { spend: money(pace.spend), allowance: money(allowance) });
  }
}


/** The viewer's relation to the group, in words: owner, member, both, neither but above it, or seen as part of the whole organisation. */
export function roleText(t: Translate, group: MyGroupUsageResponse): string {
  const kind = t(kindKeys[group.kind]).toLowerCase();
  return group.isOwner && group.isMember ? t("myGroups.role.both", { kind })
    : group.isOwner ? t("myGroups.role.owner", { kind })
    : group.isMember ? t("myGroups.role.member", { kind })
    : group.isOrganisationView ? t("myGroups.role.organisation")
    : t("myGroups.role.within");
}
